import React, { useCallback, useEffect, useState } from 'react';
import AccountCircle from '@mui/icons-material//AccountCircle';
import {
  Autocomplete,
  Box,
  Chip,
  createFilterOptions,
  FilterOptionsState,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import useGetCompanyUsers from 'services/hooks/useGetCompanyUsers';
import { useGetFirmUsersSlim } from 'services/hooks/useGetFirmUsers';
import { Logger } from 'utilities';
import { CreateNewUserDialog } from '../CreateNewUserDialog';
import { ResponsibleUser, SourceResponsibleSelectorProps } from './types';
import { mergeResponsibleUsers } from './utils';
import { ResponsibleNewUserData, ResponsibleNewUserName } from '../CreateNewUserDialog/CreateNewUserDialog.props';

const StyledTypographyLabel = styled(Typography)(() => ({
  fontWeight: 'bold',
  marginBottom: 20,
}));

const StyledAccountCircle = styled(AccountCircle)(() => ({
  marginRight: 10,
}));

const SourceResponsibleSelector = ({
  responsibleUser,
  setResponsibleUser,
  responsibleUsers,
  onResponsibleUsersReload,
  currentFirmId,
  currentCompanyId,
  currentCompanyInfo,
  is409a,
  setIsSaving,
  isDisabled,
}: SourceResponsibleSelectorProps) => {
  const [newUser, setNewUser] = useState<ResponsibleNewUserData>();
  const [openUserDetailsDialog, setOpenUserDetailsDialog] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<ResponsibleUser[]>(responsibleUsers);
  const { enqueueSnackbar } = useSnackbar();

  const { getFirmUsersSlim } = useGetFirmUsersSlim(currentFirmId, '');
  const { getCompanyUsers } = useGetCompanyUsers(currentCompanyId, '');

  const selectedResponsibleUser = availableUsers.find(user => user.value === responsibleUser);

  const getCreatedResponsibleUser = useCallback(
    (newUserId: number, createdUser: ResponsibleNewUserData): ResponsibleUser => ({
      value: newUserId,
      label: `${createdUser.firstName ?? ''} ${createdUser.lastName ?? ''}`.trim(),
      email: createdUser.email,
      isPrimaryContact: !!createdUser.isPrimaryContact,
    }),
    []
  );

  const parseName = useCallback((text: string): ResponsibleNewUserName => {
    const fullName = text.slice(4).trim(); // Remove "Add " and trim any extra spaces
    const [firstName, ...lastNameParts] = fullName.split(' ');

    return {
      fullName,
      firstName,
      lastName: lastNameParts.join(' '), // Combine the remaining parts as the last name
    };
  }, []);

  const handleOnChange = (option: ResponsibleUser | null): void => {
    if (option && option.value !== 0) {
      setResponsibleUser(option.value);
    } else {
      const name = parseName(option?.label ?? '');
      setNewUser(name);
      setOpenUserDetailsDialog(true);
    }
  };

  const reloadResponsibleUsers = useCallback(
    async (newUserId: number, createdUser?: ResponsibleNewUserData) => {
      try {
        enqueueSnackbar('Loading users...', {
          variant: 'info',
        });
        setIsSaving(true);
        const [companyUsers, firmUsers] = await Promise.all([
          currentCompanyId ? getCompanyUsers() : Promise.resolve([]),
          getFirmUsersSlim(),
        ]);

        const mergedUsers = mergeResponsibleUsers(companyUsers, firmUsers);
        const createdResponsibleUser = createdUser
          ? getCreatedResponsibleUser(newUserId, createdUser)
          : undefined;

        const nextAvailableUsers
          = createdResponsibleUser && !mergedUsers.some(user => user.value === newUserId)
            ? [...mergedUsers, createdResponsibleUser]
            : mergedUsers;

        setAvailableUsers(nextAvailableUsers);
        onResponsibleUsersReload(nextAvailableUsers);
        setResponsibleUser(newUserId);
      } catch (error) {
        Logger.error('Error loading users:', error);
      } finally {
        setIsSaving(false);
        enqueueSnackbar('Users loaded successfully', {
          variant: 'success',
        });
      }
    },
    [
      currentCompanyId,
      enqueueSnackbar,
      getCompanyUsers,
      getCreatedResponsibleUser,
      getFirmUsersSlim,
      onResponsibleUsersReload,
      setIsSaving,
      setResponsibleUser,
    ]
  );

  const handleOnCloseDialog = (newUserId?: number) => {
    setOpenUserDetailsDialog(false);
    if (newUserId && newUser) {
      const createdResponsibleUser = getCreatedResponsibleUser(newUserId, newUser);
      const nextAvailableUsers = [createdResponsibleUser, ...availableUsers.filter(user => user.value !== newUserId)];

      setAvailableUsers(nextAvailableUsers);
      onResponsibleUsersReload(nextAvailableUsers);
      setResponsibleUser(newUserId);
      reloadResponsibleUsers(newUserId, newUser);
    }
  };

  // Update availableUsers when responsibleUsers changes
  useEffect(() => {
    if (responsibleUsers?.length > 0) {
      setAvailableUsers(responsibleUsers);
    }
  }, [responsibleUsers]);

  const filter = createFilterOptions<ResponsibleUser>();

  const getFilteredOptions = useCallback(
    (options: ResponsibleUser[], params: FilterOptionsState<ResponsibleUser>) => {
      const filtered = filter(options, params);

      const { inputValue } = params;
      // Suggest the creation of a new value
      const isExisting = options.some(option => inputValue === option.label);

      if (inputValue !== '' && !isExisting) {
        const cleanInputValue = inputValue.replace(/^Add\s+/, '');
        filtered.push({
          value: 0,
          label: `Add ${cleanInputValue}`,
          isPrimaryContact: false,
        });
      }

      return filtered;
    },
    [filter]
  );

  return (
    <div>
      <StyledTypographyLabel variant="body1">Select source responsible for sending information</StyledTypographyLabel>

      <Autocomplete
        id="Select source responsible for sending information"
        options={availableUsers}
        style={{ width: '100%' }}
        autoHighlight
        getOptionLabel={option => option.label}
        onChange={(_event, option) => handleOnChange(option)}
        disableClearable
        clearOnEscape
        disabled={isDisabled}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={selectedResponsibleUser}
        filterOptions={getFilteredOptions}
        renderInput={params => (
          <TextField {...params} label="Select a responsible or add a new one" variant="standard" />
        )}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <Box
              key={option.value}
              {...rest}
              display="flex"
              alignItems="center"
              width="100%"
              justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <StyledAccountCircle />
                <Typography variant="body1" noWrap>
                  {option.label}
                </Typography>
              </Box>
              {option.isPrimaryContact && <Chip label="Primary Contact" color="primary" size="small" />}
            </Box>
          );
        }}
      />
      {newUser && (
        <CreateNewUserDialog
          open={openUserDetailsDialog}
          onClose={handleOnCloseDialog}
          newUser={newUser}
          currentFirmId={currentFirmId}
          currentCompanyInfo={currentCompanyInfo}
          is409a={is409a}
        />
      )}
    </div>
  );
};

export default SourceResponsibleSelector;
